import { useEffect } from 'react';
import {
  ComponentPropsContext,
  SitecoreContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import { SiteContext } from '@gn-audio/jss-core';

import { fixMultisiteContext, SUPPORT_SITE_NAME } from 'common/pages/multisite-utils';
import { JabraPageProps } from 'common/types/jabraPageProps';
import { sitemapFetcher } from 'lib/sitemap-fetcher';
import Layout from 'src/Layout';
import { componentBuilder } from 'temp/componentBuilder';
import { getDefaultPageProps, REVALIDATE_INTERVAL } from 'utils/sitecorePageHelpers';

const SitecorePage = ({
  componentProps,
  layoutData,
  headLinks,
  publicUrl,
  appSettings,
}: JabraPageProps): React.JSX.Element => {
  const router = useRouter();
  useEffect(() => {
    // Only refresh editor fast refresh in development mode
    if (process.env.NODE_ENV === 'development') {
      handleEditorFastRefresh();
    }
  }, []);

  useEffect(() => {
    if (!layoutData.sitecore.route) {
      router.replace('/');
    }
  }, [layoutData.sitecore.route, router]);

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        <Layout
          layoutData={layoutData}
          headLinks={headLinks}
          publicUrl={publicUrl}
          appSettings={appSettings}
        />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async (context) => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  if (process.env.NODE_ENV !== 'development' && !process.env.DISABLE_SSG_FETCH) {
    try {
      // Note: Next.js runs export in production mode
      // Sitemap fetcher returns paths for all sites prefixed with the site name; keep only this site's paths and strip the prefix
      paths = (await sitemapFetcher.fetch(context))
        .filter((p) => (p.params.path as string[])[0] === SUPPORT_SITE_NAME)
        .map((p) => ({
          ...p,
          params: { ...p.params, path: (p.params.path as string[]).slice(1) },
        }));
    } catch (error) {
      console.log('Error occurred while fetching static paths');
      console.log(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps<JabraPageProps> = async (context) => {
  fixMultisiteContext(context, SUPPORT_SITE_NAME);
  const pageProps = await getDefaultPageProps(context, true, SiteContext.Jabra);

  try {
    if (pageProps.redirectResult) {
      return pageProps.redirectResult;
    }
  } catch (error) {
    console.error('Redirect error:', error);
    throw error;
  }

  return {
    props: pageProps.jabraPageProps,
    revalidate: REVALIDATE_INTERVAL,
    notFound: pageProps.sitecorePageProps?.notFound,
  };
};

export default SitecorePage;
